import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import { ArrowLeft, ArrowRight } from "lucide-react";

import Input from "../../../../components/Input";
import Select from "../../../../components/Select";
import Upload from "../../../../components/Upload";
import { UIButton } from "../../../../components/ui";
import messages from "../../../../assets/locale/messages";
import {
  createCompany,
  fetchCompanyPortalCities,
} from "../../../../network/apis/companyPortalRegistration";
import { COMPANY_SIGNUP_REGISTERED_CITIES_QUERY } from "../../../../utils/companyRegistration";
import { acceptedPdfFilesMaxSize } from "../../../../utils/Constants";
import { ARABIC_REGEX, ENGLISH_REGEX } from "../../../../utils/Patterns";
import { uploadToS3, directories } from "../../../../utils/S3";
import { showErrorMsg } from "../../../../utils/Helpers";
import store from "../../../../store";
import { showHideLoader } from "../../../../store/Loader/actions";

import "./CompanyForm.scss";

/**
 * Display name for city dropdown from API (bilingual fields or single `name`).
 */
const getCityOptionLabel = (city, lang) => {
  if (!city) return "";
  if (lang === "ar") {
    return city.name_ar ?? city.name ?? city.name_en ?? "";
  }
  return city.name_en ?? city.name ?? city.name_ar ?? "";
};

const mapCitiesToSelectOptions = (cities, lang) =>
  (cities || []).map((c) => ({
    id: c.id,
    name: getCityOptionLabel(c, lang),
  }));

/** Delegation & ID uploads: PDF, JPG, PNG only — max 5MB (per product spec). */
const REGISTRATION_DOC_ALLOWED_MIME = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
]);

const registrationDocumentSchema = (requiredMessage) =>
  Yup.mixed()
    .nullable()
    .test("required", requiredMessage, (value) => value != null)
    .test("type", "registrationDocumentTypeInvalid", (value) => {
      if (value == null || !(value instanceof File)) return true;
      return REGISTRATION_DOC_ALLOWED_MIME.has(value.type);
    })
    .test("size", "registrationDocumentMaxSize", (value) => {
      if (value == null || !(value instanceof File)) return true;
      return value.size <= acceptedPdfFilesMaxSize;
    });

/**
 * Company details step — POST /v1/companies (fields per OpenAPI: image_url, name_en, name_ar,
 * city_id, commercial_record, commercial_record_url).
 */
const CompanyForm = ({ onComplete }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { company: t } = messages[lang];

  const validationSchema = useMemo(
    () =>
      Yup.object({
        enName: Yup.string()
          .trim()
          .required("enNameRequired")
          .matches(ENGLISH_REGEX, "englishLettersOnly"),
        arName: Yup.string()
          .trim()
          .test("arNameOptional", "arabicLettersOnly", (val) => {
            const s = val ?? "";
            return s === "" || ARABIC_REGEX.test(s);
          }),
        city: Yup.mixed()
          .nullable()
          .test(
            "city",
            "cityRequired",
            (val) =>
              val != null &&
              typeof val === "object" &&
              val.id != null &&
              val.id !== "",
          ),
        commercialRecord: Yup.string()
          .trim()
          .required("commercialRecordRequired")
          .matches(/^\d{10}$/, "commercialRecordTenDigitsOnly")

          .max(100, "maxHundredChars"),
        companyLogo: Yup.mixed()
          .nullable()
          .test(
            "logoDimensions",
            "logoDimensionsError",
            (file) =>
              new Promise((resolve) => {
                if (!file) return resolve(true);
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                  URL.revokeObjectURL(url);
                  resolve(img.width <= 800 && img.height <= 400);
                };
                img.onerror = () => {
                  URL.revokeObjectURL(url);
                  resolve(true);
                };
                img.src = url;
              }),
          ),
        crDocument: Yup.mixed()
          .nullable()
          .test("crPdf", "crDocumentRequired", (value) => value != null),
        personalIdUrl: registrationDocumentSchema("personalIdDocumentRequired"),
        delegationDocument: registrationDocumentSchema("delegationDocumentRequired"),
      }),
    [lang],
  );

  const [cities, setCities] = useState(
    /** @type {Array<{ id?: string|number, name_en?: string, name_ar?: string, name?: string }>} */ ([]),
  );
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  /** Data URL previews for PDF-or-image uploads. */
  const [personalIdPreview, setPersonalIdPreview] = useState("");
  // const [delegationPreview, setDelegationPreview] = useState("");
  const [countriesSearchInput, setCountriesSearchInput] = useState("");
  const [countriesSearchDebounced, setCountriesSearchDebounced] = useState("");
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [uploadState, setUploadState] = useState({
    target: null,
    percent: 0,
  });
  const [uploadCompleted, setUploadCompleted] = useState({
    delegation: false,
    cr: false,
    personalId: false,
    logo: false,
  });

  const cityOptions = useMemo(
    () => mapCitiesToSelectOptions(cities, lang),
    [cities, lang],
  );

  const uploadFileToS3 = async (file, name, directory, options = {}) => {
    const uploaded = await uploadToS3(file, name, directory, options);
    return uploaded?.link ?? null;
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isValid,
    dirty,
    isSubmitting,
  } = useFormik({
    initialValues: {
      enName: "",
      arName: "",
      city: null,
      commercialRecord: "",
      companyLogo: null,
      crDocument: null,
      delegationDocument: null,
      personalIdUrl: null,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (formValues, { setSubmitting }) => {
      const crFile = formValues.crDocument;
      const delegationDocumentFile = formValues.delegationDocument;
      const personalIdFile = formValues.personalIdUrl;
      const logoFile = formValues.companyLogo;
      const city = formValues.city;
      if (!city?.id || !crFile) {
        setSubmitting(false);
        return;
      }
      try {
        let image_url = null;
        if (logoFile) {
          const logoExt = logoFile.name.split(".").pop();
          const logoName = `logo.${logoExt}`;
          setUploadState({ target: "logo", percent: 0 });
          image_url = await uploadFileToS3(
            logoFile,
            logoName,
            directories.companyRegistration,
            {
              onProgress: (percent) =>
                setUploadState({ target: "logo", percent }),
            },
          );
          setUploadCompleted((prev) => ({ ...prev, logo: true }));
        }

        /** Document names are descriptive only — the backend prefixes a random segment, so nothing can overwrite. */
        const fileExt = (file) =>
          String(file?.name?.split(".").pop() || "").toLowerCase() || "pdf";

        setUploadState({ target: "cr", percent: 0 });
        const commercial_record_url = await uploadFileToS3(
          crFile,
          `commercial_record.${fileExt(crFile)}`,
          directories.companyRegistration,
          {
            onProgress: (percent) => setUploadState({ target: "cr", percent }),
          },
        );
        setUploadCompleted((prev) => ({ ...prev, cr: true }));
        setUploadState({ target: "delegation", percent: 0 });
        const delegation_document_url = await uploadFileToS3(
          delegationDocumentFile,
          `delegation.${fileExt(delegationDocumentFile)}`,
          directories.companyRegistration,
          {
            onProgress: (percent) =>
              setUploadState({ target: "delegation", percent }),
          },
        );
        setUploadCompleted((prev) => ({ ...prev, delegation: true }));
        setUploadState({ target: "personalId", percent: 0 });
        const personal_id_url = await uploadFileToS3(
          personalIdFile,
          `personal_id.${fileExt(personalIdFile)}`,
          directories.companyRegistration,
          {
            onProgress: (percent) =>
              setUploadState({ target: "personalId", percent }),
          },
        );
        setUploadCompleted((prev) => ({ ...prev, personalId: true }));

        const data = await createCompany({
          name_en: formValues.enName.trim(),
          name_ar: formValues.arName.trim(),
          city_id: city.id,
          commercial_record: formValues.commercialRecord.trim(),
          commercial_record_url,
          delegation_document_url,
          personal_id_url,
          image_url,
        });
        onComplete(data);
      } catch (error) {
        setUploadCompleted({
          delegation: false,
          cr: false,
          personalId: false,
          logo: false,
        });
        let msg = error.response?.data?.message ?? error.message ?? "";
        if (Array.isArray(msg)) {
          msg = msg.filter(Boolean).join(", ");
        }
        if (typeof msg !== "string") {
          msg = String(msg);
        }
        const normalized = msg.trim();
        if (normalized) {
          showErrorMsg(normalized);
        }
      } finally {
        setUploadState({ target: null, percent: 0 });
        setSubmitting(false);
        store.dispatch(showHideLoader(false));
      }
    },
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setCountriesSearchDebounced(countriesSearchInput);
    }, 300);
    return () => clearTimeout(t);
  }, [countriesSearchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCountriesLoading(true);
      try {
        const q = countriesSearchDebounced.trim();
        const params = {
          ...COMPANY_SIGNUP_REGISTERED_CITIES_QUERY,
          ...(q ? { name: q } : {}),
        };
        const list = await fetchCompanyPortalCities(params);
        if (!cancelled) setCities(list);
      } catch (error) {
        let msg = error.response?.data?.message ?? error.message ?? "";
        if (Array.isArray(msg)) {
          msg = msg.filter(Boolean).join(", ");
        }
        if (typeof msg !== "string") {
          msg = String(msg);
        }
        const normalized = msg.trim();
        if (normalized) {
          showErrorMsg(normalized);
        }
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countriesSearchDebounced]);

  useEffect(() => {
    if (!values.city?.id || !cityOptions.length) return;
    const match = cityOptions.find((o) => o.id === values.city.id);
    if (match && match.name !== values.city.name) {
      setFieldValue("city", match);
    }
  }, [lang, cityOptions, values.city, setFieldValue]);

  useEffect(() => {
    if (!values.companyLogo) {
      setLogoPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(values.companyLogo);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [values.companyLogo]);

  const handleLogoUploadChange = (_preview, file) => {
    // shouldValidate=false: validating here would check the previous logo, and the async
    // dimensions test would finish after setFieldValue's pass and bring back a stale error.
    setFieldTouched("companyLogo", true, false);
    setUploadCompleted((prev) => ({ ...prev, logo: false }));
    if (file) {
      setFieldValue("companyLogo", file);
    } else {
      setFieldValue("companyLogo", null);
    }
  };

  const handleCrUploadChange = (_preview, file) => {
    setFieldTouched("crDocument", true);
    setUploadCompleted((prev) => ({ ...prev, cr: false }));
    if (!file) {
      setFieldValue("crDocument", null);
      return;
    }
    setFieldValue("crDocument", file);
  };

  const handlePersonalIdUploadChange = (preview, file) => {
    setFieldTouched("personalIdUrl", true);
    setUploadCompleted((prev) => ({ ...prev, personalId: false }));
    if (!file) {
      setFieldValue("personalIdUrl", null);
      setPersonalIdPreview("");
      return;
    }
    setFieldValue("personalIdUrl", file);
    setPersonalIdPreview(typeof preview === "string" ? preview : "");
  };

  const handleDelegationDocumentUploadChange = (preview, file) => {
    setFieldTouched("delegationDocument", true);
    setUploadCompleted((prev) => ({ ...prev, delegation: false }));
    if (!file) {
      setFieldValue("delegationDocument", null);
      return;
    }
    setFieldValue("delegationDocument", file);
  };

  const logoUploadArea = (
    <div className="company-form-step__logo-dropzone">
      <div className="company-form-step__cr-dropzone-icon-wrap">
        <CloudUploadOutlined className="company-form-step__cr-dropzone-icon" />
      </div>
      <p className="company-form-step__cr-dropzone-line mb-0 text-center">
        <span className="company-form-step__cr-dropzone-action">
          {t.uploadFile.clickToUpload}
        </span>
        <span className="company-form-step__cr-dropzone-rest">
          {" "}
          {t.uploadFile.dragHint}
        </span>
      </p>
      <p className="company-form-step__cr-dropzone-hint mb-0 text-center">
        {t.logo.hint}
      </p>
    </div>
  );

  const uploadProgressLabel =
    uploadState.target != null
      ? `${t.uploadFile.uploading} ${uploadState.percent}%`
      : undefined;

  const fieldUploadProgress = (target) =>
    uploadState.target === target ? uploadState.percent : undefined;

  const fieldUploadProgressLabel = (target) =>
    uploadState.target === target ? uploadProgressLabel : undefined;

  const crUploadArea = (
    <div className="company-form-step__cr-dropzone">
      <div className="company-form-step__cr-dropzone-icon-wrap">
        <CloudUploadOutlined className="company-form-step__cr-dropzone-icon" />
      </div>
      <p className="company-form-step__cr-dropzone-line mb-0 text-center">
        <span className="company-form-step__cr-dropzone-action">
          {t.uploadFile.clickToUpload}
        </span>
        <span className="company-form-step__cr-dropzone-rest">
          {" "}
          {t.uploadFile.dragHint}
        </span>
      </p>
      <p className="company-form-step__cr-dropzone-hint mb-0 text-center">
        {t.uploadFile.hint}
      </p>
    </div>
  );

  const delegationUploadArea = (
    <div className="company-form-step__cr-dropzone">
      <div className="company-form-step__cr-dropzone-icon-wrap">
        <CloudUploadOutlined className="company-form-step__cr-dropzone-icon" />
      </div>
      <p className="company-form-step__cr-dropzone-line mb-0 text-center">
        <span className="company-form-step__cr-dropzone-action">
          {t.delegationDocumentUrl.clickToUpload}
        </span>
        <span className="company-form-step__cr-dropzone-rest">
          {" "}
          {t.delegationDocumentUrl.dragHint}
        </span>
      </p>
      <p className="company-form-step__cr-dropzone-hint mb-0 text-center">
        {t.delegationDocumentUrl.hint}
      </p>
    </div>
  );

  const personalIdUploadArea = (
    <div className="company-form-step__cr-dropzone">
      <div className="company-form-step__cr-dropzone-icon-wrap">
        <CloudUploadOutlined className="company-form-step__cr-dropzone-icon" />
      </div>
      <p className="company-form-step__cr-dropzone-line mb-0 text-center">
        <span className="company-form-step__cr-dropzone-action">
          {t.personalIdUrl.clickToUpload}
        </span>
        <span className="company-form-step__cr-dropzone-rest">
          {" "}
          {t.personalIdUrl.dragHint}
        </span>
      </p>
      <p className="company-form-step__cr-dropzone-hint mb-0 text-center">
        {t.personalIdUrl.hint}
      </p>
    </div>
  );

  return (
    <div className="company-form-step w-100">
      <div>
        <h2 className="step-title">{t.title}</h2>
        <p className="step-description mb-0">{t.description}</p>
      </div>

      <form
        className="step-form-content"
        onSubmit={handleSubmit}
        noValidate
        aria-busy={isSubmitting}
      >
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <Input
              id="company-en-name"
              name="enName"
              label={t.enName.label}
              placeholder={t.enName.placeholder}
              type="text"
              value={values.enName}
              onChange={(value) => {
                setFieldTouched("enName");
                setFieldValue("enName", value);
              }}
              onBlur={handleBlur}
              inputWrapperClass="mb-0"
              labelClassName="font-medium mb-2"
              isInputHasErr={!!(touched.enName && errors.enName)}
              errMsg={errors.enName}
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="col-12 col-md-6">
            <Input
              id="company-ar-name"
              name="arName"
              label={t.arName.label}
              placeholder={t.arName.placeholder}
              type="text"
              value={values.arName}
              onChange={(value) => {
                setFieldTouched("arName");
                setFieldValue("arName", value);
              }}
              onBlur={handleBlur}
              inputWrapperClass="mb-0"
              labelClassName="font-medium mb-2"
              isInputHasErr={!!(touched.arName && errors.arName)}
              errMsg={errors.arName}
              disabled={isSubmitting}
            />
          </div>

          <div className="col-12">
            <Select
              id="company-city"
              name="city"
              label={t.city.label}
              placeholder={t.city.placeholder}
              options={cityOptions}
              value={values.city}
              loading={countriesLoading}
              filterOptions={(opts) => opts}
              onInputChange={(_e, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setCountriesSearchInput(value ?? "");
                }
              }}
              onChange={(option) => {
                setFieldTouched("city", true);
                setFieldValue("city", option);
              }}
              onBlur={handleBlur}
              labelClassName="font-medium mb-2"
              wrapperClass="mb-0"
              hasError={!!(touched.city && errors.city)}
              errMsg={errors.city}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="col-12">
            <Input
              id="company-commercial-record"
              name="commercialRecord"
              label={t.commercialRecord.label}
              placeholder={t.commercialRecord.placeholder}
              type="text"
              inputMode="numeric"
              maxLength={10}
              value={values.commercialRecord}
              onChange={(value) => {
                const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
                setFieldTouched("commercialRecord");
                setFieldValue("commercialRecord", digitsOnly);
              }}
              onBlur={handleBlur}
              inputWrapperClass="mb-0"
              labelClassName="font-medium mb-2"
              isInputHasErr={
                !!(touched.commercialRecord && errors.commercialRecord)
              }
              errMsg={errors.commercialRecord}
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="col-12">
            <Upload
              id="delegation-document-url"
              name="delegationDocument"
              className="company-form-step__cr-upload w-100"
              wrapperClasses="w-100"
              isPdf
              accept="application/pdf"
              fieldLabel={t.delegationDocumentUrl.label}
              uploadArea={delegationUploadArea}
              pdf={values.delegationDocument}
              onChange={handleDelegationDocumentUploadChange}
              isInputHasErr={
                !!(touched.delegationDocument && errors.delegationDocument)
              }
              errorMsg={
                typeof errors.delegationDocument === "string"
                  ? errors.delegationDocument
                  : undefined
              }
              disabled={isSubmitting}
              isUploading={uploadState.target === "delegation"}
              uploadProgress={fieldUploadProgress("delegation")}
              uploadProgressLabel={fieldUploadProgressLabel("delegation")}
              uploadCompleted={uploadCompleted.delegation}
              required
            />
          </div>
          <div className="col-12">
            <Upload
              id="personal-id-url"
              name="personalIdUrl"
              className="company-form-step__logo-upload company-form-step__logo-upload--full w-100"
              wrapperClasses="w-100"
              isFullWidthImage
              fullWidthImageClassName="company-form-step__logo-preview"
              accept="application/pdf,image/jpeg,image/png,image/jpg"
              fieldLabel={t.personalIdUrl.label}
              uploadArea={personalIdUploadArea}
              isPdf
              isImage
              img={personalIdPreview}
              pdf={values.personalIdUrl}
              onChange={handlePersonalIdUploadChange}
              isInputHasErr={
                !!(touched.personalIdUrl && errors.personalIdUrl)
              }
              errorMsg={
                typeof errors.personalIdUrl === "string"
                  ? errors.personalIdUrl
                  : undefined
              }
              disabled={isSubmitting}
              isUploading={uploadState.target === "personalId"}
              uploadProgress={fieldUploadProgress("personalId")}
              uploadProgressLabel={fieldUploadProgressLabel("personalId")}
              uploadCompleted={uploadCompleted.personalId}
              required
            />
          </div>

          <div className="col-12">
            <Upload
              id="registration-cr-upload"
              name="crDocument"
              className="company-form-step__cr-upload w-100"
              wrapperClasses="w-100"
              isPdf
              accept="application/pdf"
              fieldLabel={t.uploadFile.label}
              uploadArea={crUploadArea}
              pdf={values.crDocument}
              onChange={handleCrUploadChange}
              isInputHasErr={!!(touched.crDocument && errors.crDocument)}
              errorMsg={
                typeof errors.crDocument === "string"
                  ? errors.crDocument
                  : undefined
              }
              disabled={isSubmitting}
              isUploading={uploadState.target === "cr"}
              uploadProgress={fieldUploadProgress("cr")}
              uploadProgressLabel={fieldUploadProgressLabel("cr")}
              uploadCompleted={uploadCompleted.cr}
              required
            />
          </div>

          <div className="col-12">
            <Upload
              id="registration-company-logo"
              name="companyLogo"
              className="company-form-step__logo-upload company-form-step__logo-upload--full w-100"
              wrapperClasses="w-100"
              isImage
              isFullWidthImage
              fullWidthImageClassName="company-form-step__logo-preview"
              accept="image/jpeg,image/png,image/jpg,image/bmp,image/gif,image/svg+xml"
              fieldLabel={t.logo.label}
              uploadArea={logoUploadArea}
              img={logoPreviewUrl}
              onChange={handleLogoUploadChange}
              isInputHasErr={!!(touched.companyLogo && errors.companyLogo)}
              errorMsg={
                typeof errors.companyLogo === "string"
                  ? errors.companyLogo
                  : undefined
              }
              disabled={isSubmitting}
              isUploading={uploadState.target === "logo"}
              uploadProgress={fieldUploadProgress("logo")}
              uploadProgressLabel={fieldUploadProgressLabel("logo")}
              uploadCompleted={uploadCompleted.logo}
            />
          </div>
        </div>

        <div className="mt-4">
          <UIButton
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={!dirty || !isValid || isSubmitting}
            endIcon={
              lang === "ar" ? <ArrowLeft size={16} /> : <ArrowRight size={16} />
            }
          >
            {t.actions.createCompany}
          </UIButton>
        </div>
      </form>
    </div>
  );
};

CompanyForm.propTypes = {
  onComplete: PropTypes.func,
};

CompanyForm.defaultProps = {
  onComplete: () => {},
};

export default CompanyForm;
